import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { Auth0Account } from 'src/app/accounts/interfaces/accounts.types';
import { MeService } from 'src/app/me/services/me.service';
import { OnboardingIntentStep } from 'src/app/shared/enums/intent.enum';
import { IntentStrategyService } from 'src/app/shared/services/intent/intent-strategy.service';
import { IntentService } from 'src/app/shared/services/intent/intent.service';
import { openExternalWebPage } from 'src/app/shared/util/link-util';
import { environment } from 'src/environments/environment';
import { Layout, NavigationExtras } from '../../interfaces';
import { LayoutService } from '../../services/layout.service';
import { SideMenuService } from '../../services/side-menu.service';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnChanges {
  @Input() account: Auth0Account;

  layout: Layout;
  extras: NavigationExtras;
  simple = false;
  hasSideMenu = false;
  isCollapsable = false;
  isCheckout = false;

  constructor(
    private router: Router,
    private layoutService: LayoutService,
    private meService: MeService,
    private sideMenuService: SideMenuService,
    private intentService: IntentService,
    private intentStrategyService: IntentStrategyService
  ) {}

  ngOnInit() {
    this.layoutService.layout$
      .pipe(untilDestroyed(this))
      .subscribe((layout: Layout) => {
        this.layout = layout;
        this.updateState();
      });
    this.layoutService.navigationExtras$
      .pipe(untilDestroyed(this))
      .subscribe((extras: NavigationExtras) => {
        this.extras = extras;
        this.updateState();
      });
    this.sideMenuService.isCollapsable$.subscribe(
      (collapsable) => (this.isCollapsable = collapsable)
    );
  }

  ngOnChanges() {
    this.updateState();
  }

  redirectToWebsiteOrDashboard() {
    // when user is onboarding, click in logo should take him to website (open in new tab)
    if (
      this.router.url.startsWith('/accounts') ||
      this.router.url.startsWith('/onboarding') ||
      this.router.url.startsWith('/nipt/nipt-expired')
    ) {
      openExternalWebPage(environment.pages.home);
    } else {
      this.onCancelIntentClick();
    }
  }

  openSideMenu() {
    this.sideMenuService.openToggle();
  }

  logout(): void {
    this.meService.logout();
    this.router.navigate(['accounts/logged-out']);
  }

  isRouterLinkActive(url: string): boolean {
    return this.router.isActive(url, false);
  }

  onNavItemClicked() {
    this.sideMenuService.setDefault();
  }

  onSkipClick() {
    if (
      this.layout?.header?.skipLink?.step ===
      OnboardingIntentStep.DownloadAfterCheck
    ) {
      this.intentStrategyService.context.onDownloadAfterCheckSkipClick();
    } else {
      this.intentStrategyService.context.onTrackSkipClick();
    }
  }

  onClose() {
    if (this.layout?.header?.cancelIntent) {
      this.onCancelIntentClick();
    } else if (this.layout?.header?.closeButton) {
      this.layoutService.headerCloseClick();
    }
  }

  private onCancelIntentClick() {
    this.intentService
      .cancelIntent()
      .pipe(
        finalize(() => {
          this.router.navigate(['/dashboard#overview']);
        })
      )
      .subscribe();
  }

  private updateState() {
    this.isCheckout = this.layout?.footer?.bottomOffset === 'checkout';
    this.simple = !this.account || this.layout?.header?.simple;
    this.hasSideMenu = this.account && !this.layout?.sideMenu?.hide;
    this.layoutService.headerResized$.next();
  }
}
